import { Select, SelectItem, Text } from "@ui-kitten/components";
import { BaseCardListItem } from "components/BaseCard";
import { Image } from "expo-image";
import {
  AccountTicketDetails,
  EventDetails,
  PlanPackageComponentDetails,
  SubscriptionDetails,
  TicketCartDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { getPlanType } from "utils/planPackageUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
import toast from "utils/toast";
import { getUserName } from "utils/userUtils";
import {
  componentNotExceedingPerEventMax,
  findComponentById,
  findUserById,
  getRemainingPerEventMaxForComponent,
  getRemainingQuantityForComponent,
  isPrimaryComponent,
} from "../helpers";

interface HoldDetailsProps {
  ticket: TicketDetails;
  planType: PlanPackageComponentDetails["type"];
  charge: TicketCartDetails | undefined;
  matchedSubscriptions: SubscriptionDetails[];
  initialIndex: number;
  selectedUsersAndComponents: number[];
  addSelectedUserOrComponent: (id: number, initialIndex: number) => void;
  guestComponents: PlanPackageComponentDetails[];
  prefillSelectorArray: number[];
  selectedSubscription: SubscriptionDetails;
}

export const RenderCardTitle = ({
  isGA,
  index,
  charge,
  ticketPrice,
}: {
  isGA: boolean;
  index: number;
  charge?: TicketCartDetails;
  ticketPrice: string | number;
}) => (
  <View
    style={{
      flexDirection: "row",
      justifyContent: "space-between",
      display: "flex",
      width: "100%",
    }}
  ></View>
);

export const FanRallyMarketPlace = ({ name }: { name: string }) => (
  <View
    style={{
      marginTop: 10,
      flexDirection: "row",
      alignItems: "center",
    }}
  >
    <Image
      source={{
        uri: "https://branding.fanrally.com/logos/fanrally_logo.svg",
      }}
      style={{
        width: 50,
        height: 35,
      }}
      contentFit="contain"
      responsivePolicy="initial"
    />

    <Text category="s1">{name}</Text>
  </View>
);

const HoldDetails: React.FC<HoldDetailsProps> = ({
  ticket,
  charge,
  planType,
  matchedSubscriptions,
  initialIndex,
  selectedUsersAndComponents,
  addSelectedUserOrComponent,
  guestComponents,
  prefillSelectorArray,
  selectedSubscription,
}) => {
  const { accountEvents: events } = useAccount();
  const { isChangingSeats, originalEventId, keptOriginalTicketIds, originalTickets } = useAppSelector(
    (state) => state.reservationFlow,
  );

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const maxSeatsAllowed = getMaxSeatsAllowedPerReservation(tenantConfig);

  const { userSelfRequestCompTicketsEnabled } =
    selectedSubscription.planPackage;
  const { seat, id: ticketId, event } = ticket;
  const [selectedUserOrComponentId, setSelectedUserOrComponentId] =
    useState<number>();
  const [computedMembers, setComputedMembers] = useState<SubscriptionDetails[]>(
    [],
  );
  const selectedMember = computedMembers.find(
    (sub) => sub.member.user.id === selectedUserOrComponentId,
  )?.member;
  const getComponentLabel = (component?: PlanPackageComponentDetails) =>
    component?.displayName?.trim() || component?.name || "";
  const displayValue = selectedMember
    ? getUserName(selectedMember)
    : getComponentLabel(
        findComponentById(selectedUserOrComponentId || 0, matchedSubscriptions),
      );

  // returns true if there is only one user member in the selectedUsersAndComponents array
  const oneUserSelected =
    selectedUsersAndComponents.filter((userOrComponent) => {
      return findUserById(userOrComponent, matchedSubscriptions) !== undefined;
    }).length === 1;

  // returns true if there is only one primary voucher component selected among the selectors
  const onePrimaryComponentSelected = useMemo(
    () =>
      selectedUsersAndComponents.filter((component) =>
        isPrimaryComponent(component, selectedSubscription),
      ).length === 1,
    [selectedUsersAndComponents],
  );

  // Returns true if user is changing the value of the selector from a guest component
  // to another guest component
  const switchingGuest = useCallback(
    (newComponent: PlanPackageComponentDetails) => {
      if (!selectedUserOrComponentId) return false;
      // the current selected id is an user id
      if (findUserById(selectedUserOrComponentId, matchedSubscriptions))
        return false;
      // the current selected id is a primary component id
      if (isPrimaryComponent(selectedUserOrComponentId, selectedSubscription)) {
        return false;
      }
      return newComponent.type.includes("GUEST");
    },
    [selectedUserOrComponentId, selectedSubscription],
  );

  const getTicketChargeByTicketId = (ticketId: number) => {
    if (!charge) return 0;
    if (userSelfRequestCompTicketsEnabled) return 0;

    const { allInclusivePrice } = selectedSubscription.planPackage;
    const { items, feeItems } = charge;
    let totalCharge = 0;

    const chargeItem = items.find((item) => item.ticketId === ticketId);
    totalCharge = Number(chargeItem?.totalPrice);

    if (allInclusivePrice && feeItems && feeItems.length > 0) {
      feeItems
        .filter((item) => item.ticketId === ticketId)
        .forEach((item) => {
          totalCharge += Number(item?.totalPrice);
        });
    }

    return totalCharge;
  };

  // retuns undefined if selected subscripiton is RESERVATION
  const component = findComponentById(
    selectedUserOrComponentId || 0,
    matchedSubscriptions,
  );

  const componentHasNotReachedMax = (
    component: PlanPackageComponentDetails,
    isChangingSeats: boolean = false,
  ) => {
    const maxForComponent = getRemainingPerEventMaxForComponent({
      component,
      subscriptions: matchedSubscriptions,
      events,
      eventId: event.id,
      isChangingSeats,
      maxSeatsAllowed,
      groupCode: isChangingSeats ? (ticket.groupCode ?? undefined) : undefined,
      keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
    });
    const remainingQuantityForComponent = getRemainingQuantityForComponent({
      component,
      subscriptions: matchedSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: event.id,
      maxSeatsAllowed,
      groupCode: isChangingSeats ? (ticket.groupCode ?? undefined) : undefined,
      keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
    });
    const componentAlreadySelectedAmount = selectedUsersAndComponents.filter(
      (id) => id === component.id,
    ).length;

    return (
      componentAlreadySelectedAmount < maxForComponent &&
      componentAlreadySelectedAmount < remainingQuantityForComponent &&
      componentNotExceedingPerEventMax({
        component,
        accountEvents: events,
        selectedEvent: ticket.event,
        isChangingSeats,
        keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
      })
    );
  };

  const selectedPrimaryVouchersNotExceedingPerEventMax = ({
    primaryComponent,
    accountEvents = [],
    selectedEvent,
    isChangingSeats = false,
  }: {
    primaryComponent: PlanPackageComponentDetails;
    accountEvents?: AccountTicketDetails[];
    selectedEvent: EventDetails;
    isChangingSeats?: boolean;
  }) => {
    const voucherPerEventMax = matchedSubscriptions
      .filter(
        (sub) => sub.planPackage.primaryComponent.id === primaryComponent.id,
      ) // find matching subscriptions first
      .reduce((result: number, currentSub) => {
        const {
          planPackage: {
            primaryComponent: { perEventMax },
          },
        } = currentSub;
        if (perEventMax === -1) return result + maxSeatsAllowed;
        return currentSub.planPackage.primaryComponent.perEventMax + result;
      }, 0); // add all primary components' per event max
    const remainingQuantity = getRemainingQuantityForComponent({
      component: primaryComponent,
      subscriptions: matchedSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: selectedEvent.id,
      maxSeatsAllowed,
      groupCode: isChangingSeats ? (ticket.groupCode ?? undefined) : undefined,
      keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
    });

    const totalPrimaryComponentUsed = accountEvents // find the amount of all primary vouchers used for the current event
      .filter((event) => event.event.id === selectedEvent.id)
      .reduce((result: number, currentAccountEvent) => {
        let primaryComponentUsedForEvent = 0;
        for (let ticket of currentAccountEvent.tickets) {
          if (ticket.planPackageComponentId !== primaryComponent.id) continue;
          // when changing seats, only count tickets being kept (they still occupy a slot)
          if (isChangingSeats && !keptOriginalTicketIds.includes(ticket.id)) continue;
          primaryComponentUsedForEvent++;
        }
        return primaryComponentUsedForEvent + result;
      }, 0);

    // find the amount of all primary voucher selected during current reservation
    const selectedPrimaryComponentIdCount = selectedUsersAndComponents.filter(
      (id) => id === primaryComponent.id,
    ).length;
    return (
      selectedPrimaryComponentIdCount + totalPrimaryComponentUsed <
      // per event max cannot exceed the remaining quantity(available vouchers)
      Math.min(voucherPerEventMax, remainingQuantity)
    );
  };

  const componentsHaveNotReachedMax = guestComponents.filter((component) =>
    componentHasNotReachedMax(component, isChangingSeats),
  );

  // show unselected members and current selected member in select items
  useEffect(() => {
    const primaryComponentId = selectedSubscription.planPackage.primaryComponent.id;
    const primaryPerEventMax = selectedSubscription.planPackage.primaryComponent.perEventMax;

    // A member whose primary slot is occupied by a kept ticket cannot use it again (perEventMax already consumed).
    const memberHasKeptPrimaryTicket = (memberId: number): boolean => {
      if (!isChangingSeats || primaryPerEventMax < 0) return false;
      return originalTickets.some(
        (t) =>
          keptOriginalTicketIds.includes(t.id) &&
          t.planPackageComponentId === primaryComponentId &&
          t.user?.id === memberId,
      );
    };

    const newComputedMembers = matchedSubscriptions.filter(
      ({ member: { user } }) => {
        if (memberHasKeptPrimaryTicket(user.id)) return false;
        return (
          !selectedUsersAndComponents.includes(user.id) ||
          user.id === selectedUserOrComponentId
        );
      },
    );

    setComputedMembers(newComputedMembers);
  }, [selectedUsersAndComponents, selectedUserOrComponentId]);

  // pre fill selectors
  // prefillSelectorArray (computed in Reserve.tsx) is the single source of truth for what each
  // selector should default to — it already distinguishes user IDs from component IDs for all plan
  // types, including RESERVATION plans where some slots may be guest components (e.g. when the
  // user keeps their primary seat and is only adding new guest seats).
  useEffect(() => {
    if (matchedSubscriptions.length === 0) return;
    const prefillId = prefillSelectorArray[initialIndex];
    if (!prefillId) return;
    setSelectedUserOrComponentId(prefillId);
    addSelectedUserOrComponent(prefillId, initialIndex);
  }, [matchedSubscriptions]);

  // returns true if selected subscription is RESERVATION and selectedUserOrComponentId is user id
  const isReservationAndNotGuestComponent = useMemo(
    () =>
      getPlanType(selectedSubscription.planPackage) === "RESERVATION" &&
      component === undefined,
    [component],
  );

  // returns true if selected subscription is VOUCHER and selectedUserOrComponentId is the primary component id
  const isVoucherAndIsPrimary = useMemo(
    () =>
      getPlanType(selectedSubscription.planPackage) === "VOUCHER" &&
      component?.primary,
    [component],
  );

  const primaryComponent = selectedSubscription.planPackage.primaryComponent;
  return (
    <View style={{ marginBottom: 10 }}>
      <BaseCardListItem
        title={
          event.type === "RSVP"
            ? seat.sectionDisplay
            : seat.ga
              ? "General Admission"
              : `Section: ${seat.sectionDisplay} Row: ${seat.rowDisplay} ${
                  ticket.provider === "TFL" ? "" : `Seat: ${seat.seatDisplay}`
                }`
        }
        accessoryRight={
          <View>
            {charge ? (
              <Text category="s1">
                {formatCurrency(getTicketChargeByTicketId(ticket.id), {
                  priceType: charge?.priceType,
                })}
              </Text>
            ) : (
              <Text>loading...</Text>
            )}
          </View>
        }
        showArrow={false}
      />
      {Number(getTicketChargeByTicketId(ticketId)) > 0 &&
        (isReservationAndNotGuestComponent || isVoucherAndIsPrimary) && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Image
              source={require("assets/images/seat-upgrade-icon.png")}
              style={{ width: 25, height: 25 }}
              responsivePolicy="initial"
            ></Image>
            <Text>
              Seat Upgrade.{" "}
              {isReservationAndNotGuestComponent
                ? "Pass Applied"
                : isVoucherAndIsPrimary
                  ? "Credit Applied"
                  : ""}
            </Text>
          </View>
        )}
      {(planType === "RESERVATION" || guestComponents.length > 0) && (
        <Select
          testID="reserve-user-selector"
          value={displayValue}
          onSelect={() => {}}
          placeholder={i18n.t("screens.Reserve.selectGuestType")}
        >
          <>
            {planType === "RESERVATION" ? (
              computedMembers.map(({ member }, index) => (
                <SelectItem
                  testID="reserve-user-selector-item"
                  key={member.id}
                  title={getUserName(member)}
                  onPressIn={() => {
                    const newUserId = computedMembers[index]?.member.user.id;
                    if (newUserId) {
                      addSelectedUserOrComponent(newUserId, initialIndex);
                      setSelectedUserOrComponentId(newUserId);
                    }
                  }}
                ></SelectItem>
              ))
            ) : (
              // if vouchers used for selected event have not exceeded the per event max combined for component
              <>
                {componentNotExceedingPerEventMax({
                  component: primaryComponent,
                  accountEvents: events,
                  selectedEvent: event,
                  isChangingSeats,
                  keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
                }) &&
                selectedPrimaryVouchersNotExceedingPerEventMax({
                  primaryComponent,
                  accountEvents: events,
                  selectedEvent: event,
                  isChangingSeats,
                }) ? (
                  <SelectItem
                    testID="reserve-user-selector-item"
                    title={getComponentLabel(primaryComponent)}
                    onPressIn={() => {
                      const primaryComponentId = primaryComponent.id;
                      addSelectedUserOrComponent(
                        primaryComponentId,
                        initialIndex,
                      );
                      setSelectedUserOrComponentId(primaryComponentId);
                    }}
                  ></SelectItem>
                ) : null}
              </>
            )}
            {componentsHaveNotReachedMax.map((component, index) => (
              <SelectItem
                title={getComponentLabel(component)}
                key={index}
                onPressIn={() => {
                  const { id } = component;
                  // check if current selector is a user id
                  const isUserSelected =
                    findUserById(
                      selectedUserOrComponentId!,
                      matchedSubscriptions,
                    ) !== undefined;
                  // if user is not switching guest components,
                  // i.e. from paid guest -> included guest
                  // or from included guest -> paid guest
                  if (!switchingGuest(component)) {
                    if (id === selectedUserOrComponentId) return; // if user selected the same item
                    // if user is trying to switch to guest pass from a user, check how many users are selected,
                    // if only one user is selected, then we don't allow it
                    if (
                      (isUserSelected && oneUserSelected) ||
                      onePrimaryComponentSelected
                    ) {
                      toast.error(
                        i18n.t(
                          "screens.Reserve.mustHaveOneAccountMemberAttending",
                        ),
                      );
                      return;
                    }
                  }
                  addSelectedUserOrComponent(id, initialIndex);
                  setSelectedUserOrComponentId(id);
                }}
              ></SelectItem>
            ))}
          </>
        </Select>
      )}
    </View>
  );
};

export default HoldDetails;
